import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/use-is-admin";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

export function Header() {
  const { user, signOut } = useAuth();
  const { isAdmin } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  return (
    <header ref={headerRef} className="border-b border-rule bg-background/95 backdrop-blur sticky top-0 z-40">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-baseline gap-3 shrink-0">
          <span className="font-mono text-[0.72rem] font-bold tracking-[0.22em] text-primary border border-primary/70 px-2 py-1">
            TIGER&apos;S EYE
          </span>
          <span className="text-xl text-primary hidden sm:inline" style={{ fontFamily: "var(--display-font)" }}>Academic Review.</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-3 text-sm">
          {user ? (
            <>
              <Link to="/dashboard" className="px-3 py-2 text-ink-secondary hover:text-primary">Dashboard</Link>
              <Link to="/reviews" className="px-3 py-2 text-ink-secondary hover:text-primary">Reviews</Link>
              <Link to="/library" className="px-3 py-2 text-ink-secondary hover:text-primary">Library</Link>
              <Link to="/profile" className="px-3 py-2 text-ink-secondary hover:text-primary">Profile</Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="px-2 py-1 font-mono text-[0.65rem] tracking-[0.18em] border border-primary/70 text-primary hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  ADMIN
                </Link>
              )}
              <ThemeToggle />
              <Button
                variant="outline"
                size="sm"
                onClick={async () => { await signOut(); navigate({ to: "/" }); }}
              >
                Sign out
              </Button>
            </>
          ) : (
            <>
              <ThemeToggle />
              <Link to="/login">
                <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-4">
                  Sign in with Google
                </Button>
              </Link>
            </>
          )}
        </nav>

        {/* Mobile controls */}
        {user ? (
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              className="p-2 text-primary"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        ) : (
          <div className="md:hidden flex items-center gap-2">
            <ThemeToggle />
            <Link to="/login">
              <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-3">
                Sign in
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Mobile stacked nav */}
      {user && menuOpen && (
        <nav id="mobile-menu" className="md:hidden border-t border-rule bg-background px-6 py-4 shadow-sm flex flex-col gap-3 text-sm text-foreground">
          <Link
            to="/dashboard"
            className="px-3 py-2 text-ink-secondary hover:text-primary"
            onClick={() => setMenuOpen(false)}
          >
            Dashboard
          </Link>
          <Link
            to="/reviews"
            className="px-3 py-2 text-ink-secondary hover:text-primary"
            onClick={() => setMenuOpen(false)}
          >
            Reviews
          </Link>
          <Link
            to="/library"
            className="px-3 py-2 text-ink-secondary hover:text-primary"
            onClick={() => setMenuOpen(false)}
          >
            Library
          </Link>
          <Link
            to="/profile"
            className="px-3 py-2 text-ink-secondary hover:text-primary"
            onClick={() => setMenuOpen(false)}
          >
            Profile
          </Link>
          {isAdmin && (
            <Link
              to="/admin"
              className="px-3 py-2 font-mono text-xs tracking-widest text-primary"
              onClick={() => setMenuOpen(false)}
            >
              ADMIN
            </Link>
          )}
          <Button
            variant="outline"
            size="sm"
            className="w-fit mx-3"
            onClick={async () => { await signOut(); navigate({ to: "/" }); setMenuOpen(false); }}
          >
            Sign out
          </Button>
        </nav>
      )}
    </header>
  );
}
